import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import * as analytics from '../services/analytics';
import { hydrateFromBackend as hydrateCookieConsent } from '../services/cookieConsent';

const AuthContext = createContext(null);

// Token storage:
//   - sessionStorage: default (SEC-P3) — token dies with the tab.
//   - localStorage:   opt-in via the "Stay logged in" checkbox — persists
//     across tab/browser restarts. Chosen by the user on sign-in.
// A tiny "remember" marker is stored in localStorage so subsequent auth
// updates (2FA, refresh) know which channel to write back into.
const TOKEN_KEY = 'token';
const REMEMBER_KEY = 'auth_remember';

const readToken = () => {
  try {
    // Prefer persistent token when the user opted in.
    const persistent = localStorage.getItem(TOKEN_KEY);
    if (persistent) return persistent;
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const isRemembered = () => {
  try { return localStorage.getItem(REMEMBER_KEY) === '1'; } catch { return false; }
};

const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* noop */ }
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};

const storeToken = (token, remember) => {
  clearToken();
  if (remember) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REMEMBER_KEY, '1');
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me')
        .then(r => {
          setUser(r.data);
          analytics.identify(r.data.id);
          // Bring the user's cross-device cookie choice into localStorage.
          hydrateCookieConsent(r.data.cookie_consent || null);
        })
        .catch(() => { setToken(null); clearToken(); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const setUserFromToken = (newToken, userData, remember = isRemembered()) => {
    storeToken(newToken, !!remember);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    analytics.identify(userData?.id);
    hydrateCookieConsent(userData?.cookie_consent || null);
  };

  const login = async (email, password, remember = false) => {
    const res = await api.post('/auth/login', { email, password });
    // 2FA challenge — don't persist session yet; caller handles second step.
    if (res.data.requires_totp) {
      // Remember the user's "stay logged in" choice for the 2FA verify step.
      try { sessionStorage.setItem('pending_remember', remember ? '1' : '0'); } catch { /* noop */ }
      return res.data;
    }
    setUserFromToken(res.data.access_token, res.data.user, remember);
    analytics.track('login', { method: 'password' });
    return res.data;
  };

  const verifyTotpLogin = async (challenge_token, code, remember = false) => {
    const res = await api.post('/auth/login/verify-totp', { challenge_token, code });
    // Fall back to the pre-2FA remember choice if the caller didn't pass one.
    let effectiveRemember = remember;
    try {
      const pending = sessionStorage.getItem('pending_remember');
      if (pending !== null) {
        effectiveRemember = pending === '1';
        sessionStorage.removeItem('pending_remember');
      }
    } catch { /* noop */ }
    setUserFromToken(res.data.access_token, res.data.user, effectiveRemember);
    analytics.track('login', { method: 'password_totp' });
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    analytics.track('sign_up', { method: 'password' });
    return res.data;
  };

  const logout = () => {
    analytics.track('logout');
    analytics.clearIdentity();
    clearToken();
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const res = await api.get('/auth/me');
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyTotpLogin, register, logout, refreshUser, setUserFromToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
